<template>
  <div
    id="sunModel"
    class="page page-sun-model"
  >
    <content-spinner
      :show="loading"
      text="Please wait..."
      class="h-100"
    />
    <div
      v-show="!loading"
      class="page-toolbar bg-white shadow-sm p-3"
    >
      <div class="d-flex align-items-center">
        <div class="mr-2">
          <b-button-group class="mx-1">
            <b-button
              id="zoom_in"
              class="btn-zoom"
              variant="secondary"
            >
              <font-awesome-icon :icon="['fas', 'plus']" />
            </b-button>
            <b-button
              id="zoom_out"
              class="ml-2 btn-zoom"
              variant="secondary"
            >
              <font-awesome-icon :icon="['fas', 'minus']" />
            </b-button>
          </b-button-group>
        </div>
        <div class="tags-select">
          <input-widget
            :model="form"
            :field="fields.tags"
            @select="onTagSelect"
          />
        </div>
      </div>
      <b-dropdown
        variant="link"
        toggle-class="text-decoration-none"
        no-caret
      >
        <template #button-content>
          <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
        </template>
        <b-dropdown-item @click="resetView">
          Reset View
        </b-dropdown-item>
        <b-dropdown-item @click="_showHideAttributes()">
          {{ optionsFilter.displayAttributes ? 'Hide' : 'Show' }}
          Attributes
        </b-dropdown-item>
        <b-dropdown-item @click="_showHideMinorElements()">
          {{ optionsFilter.displayMinorElements ? 'Hide' : 'Show' }}
          Minor Elements
        </b-dropdown-item>
        <b-dropdown-item @click="onSlideOptionsClick">
          Slider Options
        </b-dropdown-item>
        <b-dropdown-item @click="onManageTagsClick">
          Manage Tags
        </b-dropdown-item>
      </b-dropdown>
    </div>
    <div
      id="pageContent"
      class="page-content graph-page"
      :class="{'dark-mode': optionsFilter.darkMode}"
    >
      <div
        ref="svgWrapper"
        class="svg-wrapper"
        @contextmenu.prevent="displayBackgroundContextmenu"
      >
        <svg
          id="mainSvg"
          ref="svg"
          :width="width"
          :height="height"
        />
      </div>
    </div>


    <ContextMenu
      ref="contextMenu"
      :click-object="contextMenuClickObject"
      :node="contextMenuNode"
      :show-attributes="optionsFilter.displayAttributes"
      @resetView="resetView()"
    />
    <SliderOptionsModal
      :show-attributes="optionsFilter.displayAttributes"
      :sun-model-data="sunModelData"
      @on-length-change="onLengthChange"
      @on-width-change="onWidthChange"
      @on-font-size-change="onFontSizeChange"
      @on-fact-font-size-change="onFactFontSizeChange"
    />
    <FactModal />
    <DimensionModal />
    <LinkModal />
    <MeasuresModal />
    <OptionsModal />
    <AttributesModal />
    <ConfirmModal />
    <ManageTagsModal
      :dimensions="sunModelData.dimensions"
      :facts="sunModelData.facts"
    />
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import * as d3 from 'd3';
import ContentSpinner from '../../core/components/ContentSpinner';
import FactModal from './FactModal';
import eventBus, {
  DIMENSION_ADD,
  DIMENSION_LINK_ADD,
  FACT_ADD,
  LINK_ADD,
  SHOW_ATTRIBUTES,
  SHOW_MEASURES,
  SHOW_MINOR,
} from '../../core/services/event-bus';
import DimensionModal from './DimensionModal';
import LinkModal from './LinkModal';
import MeasuresModal from './MeasuresModal';
import OptionsModal from './OptionsModal';
import AttributesModal from './AttributesModal';
import ContextMenu from './ContextMenu';
import ConfirmModal from '../../core/components/confirm-modal/ConfirmModal';
import pixelWidth from 'string-pixel-width';
import SliderOptionsModal from './SliderOptionsModal';
import ManageTagsModal from '@/modules/SunModel/ManageTagsModal';
import InputWidget from '@/core/components/complex-table/components/InputWidget';
import {tagsAsMap} from '../../store/modules/sun-model/getters';

const {mapState, mapActions, mapGetters} = createNamespacedHelpers('sunModel');

const dimensionDefaults = {
  distance: 150,
  strokeWidth: 1,
  fontSize: 8,
  area: 24,
  minWidth: 40,
  maxWidth: 200,
};

export default {
  name: 'Graph',
  components: {
    InputWidget,
    ManageTagsModal,
    SliderOptionsModal,
    DimensionModal,
    FactModal,
    ContentSpinner,
    LinkModal,
    OptionsModal,
    MeasuresModal,
    AttributesModal,
    ContextMenu,
    ConfirmModal,
  },
  data: () => {
    return {
      form: {
        tags: [],
      },
      fields: {
        tags: {
          type: 'multi-select',
          required: false,
          name: 'tags',
          config: {
            optionValue: 'value',
            optionLabel: 'name',
            placeholder: 'Filter facts by tags',
            multiple: true,
            taggable: false,
          },
          data: {
            options: [],
          },
        },
      },
      contextMenuClickObject: '',
      contextMenuNode: null,
      collapsed: {
        originalData: {},
        isCollapsed: false,
      },
      width: 1200,
      height: 800,
      nodes: [],
      links: [],
      hoverTooltipDiv: null,
      transform: {
        k: 1,
        x: 1,
        y: 1,
      },
      attributes: false,
      showMeasures: false,
      showMinor: false,
      d3: {
        radiusFact: 30,
        fontSizeFact: 8,
        radiusDimension: 10,
        radiusAttribute: 5,
        svg: null,
        g: null,
        node: null,
        simulation: null,
        link: null,
      },
      coordinates: [],
      dimension: dimensionDefaults,
      attribute: {
        distance: 150,
        strokeWidth: 1,
        fontSize: 12,
      },
      fact: {
        factFontSize: 8,
        attrFontSize: 4,
        radius: 30,
      },
    };
  },
  watch: {
    sunModelData() {
      this.fact.radius = this.optionsFilter.displayAttributes ? this.d3.radiusFact : this.fact.radius;
      this.fact.factFontSize = this.optionsFilter.displayAttributes ? 8 : this.d3.fontSizeFact;

      const nodes = [
        ...this.sunModelData.facts.map(f => ({...f, type: 'fact', radius: this.fact.radius})),
        ...this.sunModelData.dimensions.map(d => ({...d, type: 'dimension'})),
        ...[].concat.apply([], this.sunModelData.dimensions.map(a =>
          [...a.attributes.map(at => ({...at, parentType: 'dimension'}))]))
          .map(l => ({...l, type: 'attribute'})),
        ...[].concat.apply([], this.sunModelData.facts.map(a =>
          [...a.attributes.map(at => ({...at, parentType: 'fact'}))]))
          .map(l => ({...l, type: 'attribute'})),
      ];

      /*const old = new Map(this.nodes.map(d => [d.uuid, d]));
      this.nodes = nodes.map(d => Object.assign(old.get(d.id) || {}, d));*/
      this.nodes = nodes.map(d => Object.assign({}, d));

      const links = [
        ...this.sunModelData.links.filter(l => !l.alias).map(l => ({source: l.fromEntityId, target: l.toEntityId})),
        ...[].concat.apply([], this.sunModelData.dimensions.map(d => d.attributes.map(el => ({
          source: d.entityId,
          target: el.attributeId,
          type: 'attribute',
        })))),
        ...[].concat.apply([], this.sunModelData.facts.map(d => d.attributes
          .filter(a => this.isDoNotAggregate(a.aggregationType)).map(el => ({
            source: d.entityId,
            target: el.attributeId,
            type: 'attribute',
          })))),
      ];
      this.links = links.map(d => Object.assign({}, d));

      this.renderGraph();
    },
    tags: {
      immediate: true,
      deep: true,
      handler() {
        this.fields.tags.data.options = this.tags.data.map(t => ({name: t.tagName, value: t.tagId}));
      },
    },
  },
  computed: {
    ...mapState({
      loaded: state => state.sunModel.loaded,
      loading: state => state.sunModel.loading,
      sunModelData: state => state.sunModel.data,
      sunModelOriginalData: state => state.sunModel.originalData,
      nodeTypes: state => state.sunModel.nodeTypes,
      optionsFilter: state => state.sunModel.optionsFilter,
      tags: state => state.tags,
    }),
    ...mapGetters(['tagsAsMap']),
  },
  methods: {
    ...mapActions(['loadData', 'filterData', 'unloadData', 'showFactModal', 'showDimensionModal', 'showTagsModal',
      'showLinkFactModal', 'createFactTable', 'updateFilter', 'setDimensionLink', 'createLink',
      'showOptionsModal', 'getAggregationTypes', 'getDataTypes', 'showSliderModal', 'showHideAttributes', 'showHideMinorElements',
      'getTags']),
    onTagSelect(tags) {
      if (tags.length === 0) {
        this.updateFilter({filteredFacts: null});
      } else {
        const tagsValues = tags.map(tag => tag.value);
        const filteredDimensions = this.sunModelOriginalData.dimensions.filter(d => tagsValues.some(tag => d.tags.includes(tag)));
        const filteredFacts = this.sunModelOriginalData.facts.filter(fact => tagsValues.some(tag => fact.tags.includes(tag)));
        const ids = [...filteredDimensions, ...filteredFacts].map(i => i.entityId);
        this.updateFilter({filteredFacts: ids});
      }
    },
    renderGraph() {
      this.init();
      this.drawFactsAndLines();
      this.initEventListeners();
      this.forceSimulation();
    },
    init() {
      if (this.$refs.svg) {
        this.$refs.svg.innerHTML = '';
      }

      // create somewhere to put the force directed graph
      this.d3.svg = d3.select('#mainSvg');

      //add encompassing group for the zoom
      this.d3.g = this.d3.svg.append('g')
        .attr('class', 'everything');

    },
    forceSimulation() {
      const vm = this;

      //Sample links data
      //set up the simulation and add forces
      this.d3.simulation = d3.forceSimulation(this.nodes);

      const link_force = d3.forceLink(this.links).id(d => this.isAttribute(d.type) ? d.attributeId : d.entityId)
        .distance(d => this.isAttribute(d.type) ? this.attribute.distance : this.dimension.distance);
      const charge_force = d3.forceManyBody().strength(-800);
      const center_force = d3.forceCenter(this.width / 2, this.height / 2);

      this.d3.simulation
        .force('charge_force', charge_force)
        .force('center_force', center_force)
        .force('links', link_force)
      ;

      //add tick instructions:
      this.d3.simulation.on('tick', tickActions.bind(this));

      function tickActions() {
        //update circle positions each tick of the simulation
        this.d3.node.attr('transform', d => `translate(${d.x},${d.y})`);

        //update link positions
        this.d3.link
          .attr('x1', d => d.source.x)
          .attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x)
          .attr('y2', d => d.target.y)
          .attr('d', d => d3.line()([[d.source.x, d.source.y], [d.target.x, d.target.y]]));
      }
    },
    displayTooltip(d, x, y) {
      const rect = document.getElementById('pageContent').getBoundingClientRect();


      this.hoverTooltipDiv
        .style('display', 'block')
        .style('opacity', 1);
      this.hoverTooltipDiv.html(() => {
        let content = `<p>${this.isAttribute(d.type) ? d.attributeDisplayName : d.entityDisplayName}</p>
                          <p>${d.mainDimName ? `(${d.mainDimName})` : ''}</p>
                            <p>${d.description ? d.description : ''}</p>`;
        if (d.tags) {
          if (!Array.isArray(d.tags)) {
            d.tags = JSON.parse(d.tags);
          }
          d.tags.filter(tagId => this.tagsAsMap.get(tagId)).forEach(tagId => {
            const tag = this.tagsAsMap.get(tagId);
            content += `<span class="badge badge-pill badge-secondary mr-1">${tag.tagName || tagId}</span>`;
          });
        }
        return content;
      })
        .style('left', ((x - rect.left + 10) + 'px'))
        .style('top', ((y - rect.top - document.getElementsByClassName('svg-tooltip')[0].offsetHeight - 10) + 'px'));


      this.d3.node.selectAll('circle').style('opacity', o => {
        return this.isConnected(d, o) ? 1 : 0.5;
      });
      this.d3.node.selectAll('rect').style('opacity', o => {
        return this.isConnected(d, o) ? 1 : 0.5;
      });
      this.d3.node.selectAll('text').style('opacity', o => {
        return this.isConnected(d, o) ? 1 : 0.5;
      });
      this.d3.link.style('opacity', o => {
        if (this.isAttribute(d.type)) {
          return o.target.attributeId === d.attributeId ? 1 : 0.1;
        }
        return o.target.entityId === d.entityId || o.source.entityId === d.entityId ? 1 : 0.1;
      });
    },
    displayBackgroundContextmenu($event) {
      this.contextMenuClickObject = 'svg';
      this.$refs.contextMenu.$refs.menu.open($event);
    },
    drawFactsAndLines() {
      const vm = this;
      const excludes = [];
      if (!this.attributes) {
        excludes.push('attribute');
      }

      const dimStrokeWidth = parseInt(this.dimension.strokeWidth);
      const attrStrokeWidth = parseInt(this.attribute.strokeWidth);
      const dimArea = this.dimension.area;
      const getDimArrowX = this.computedDimensionArrowXCoordinate(dimStrokeWidth, dimArea);
      const getAttrArrowX = [1, 2].includes(attrStrokeWidth) ? 30 : attrStrokeWidth === 3 ? 25 : attrStrokeWidth === 4 ? 23 : 21;

      // Draw arrows for dimensions
      this.d3.g.append('defs').append('marker')
        .attr('id', 'dim-marker')
        .attr('viewBox', [0, 0, 20, 20])
        .attr('refX', getDimArrowX)
        .attr('refY', 10)
        .attr('markerWidth', dimStrokeWidth === 1 ? 10 : 5)
        .attr('markerHeight', dimStrokeWidth === 1 ? 10 : 5)
        .attr('orient', 'auto')
        .attr('fill', '#AE5A21')
        .append('path')
        .attr('d', d3.line()([[0, 0], [0, 20], [20, 10]]));

      // Draw arrows for attributes
      this.d3.g.append('defs').append('marker')
        .attr('id', 'attr-marker')
        .attr('viewBox', [0, 0, 20, 20])
        .attr('refX', getAttrArrowX)
        .attr('refY', 10)
        .attr('markerWidth', attrStrokeWidth === 1 ? 10 : 5)
        .attr('markerHeight', attrStrokeWidth === 1 ? 10 : 5)
        .attr('orient', 'auto')
        .attr('fill', '#AEAE21')
        .append('path')
        .attr('d', d3.line()([[0, 0], [0, 20], [20, 10]]));

      //draw lines for the links
      this.d3.link = this.d3.g.append('g')
        .attr('class', 'links')
        .selectAll('path')
        .data(this.links)
        .enter()
        .append('path')
        .attr('stroke-width', d => this.isAttribute(d.type) ? attrStrokeWidth : dimStrokeWidth)
        .style('stroke', linkColor.bind(this))
        .attr('marker-end', d => this.isAttribute(d.type) ? 'url(#attr-marker)' : 'url(#dim-marker)');

      // draw circles for the nodes
      this.d3.node = this.d3.g
        .selectAll('.nodes')
        .data(this.nodes)
        .enter()
        .append('g')
        .attr('class', 'nodes')
        .on('contextmenu', (d, index) => {
          d3.event.stopPropagation();
          d3.event.preventDefault();
          this.contextMenuClickObject = d.type;

          this.contextMenuNode = d;
          this.$refs.contextMenu.$refs.menu.open(d3.event, d);
        });

      // Get facts
      const facts = this.d3.node.filter(d => this.isFact(d.type));

      // Draw circle for facts
      facts.append('circle')
        .attr('r', d => d.radius)
        .attr('fill', circleColour.bind(this))
        .attr('stroke', '#2E60B6');

      // Draw text for facts
      facts.append('text')
        .selectAll()
        .data(getText.bind(this))
        .enter()
        .append('tspan')
        .attr('class', d => d.class)
        .attr('font-size', d => d.fontSize)
        .text(d => d.name)
        .attr('x', d => d.x)
        .attr('y', d => d.y)
        .attr('dy', d => d.offset);

      // Get Fact radius after draw text
      facts.selectAll('circle')
        .attr('r', d => d.radius);

      // Get dimensions
      const dimensions = this.d3.node.filter(d => this.isDim(d.type));

      // Draw rect for dimensions
      dimensions.append('rect')
        .attr('x', d => {
          const width = getWidthForDimension.call(this, d);
          return -width / 2;
        })
        .attr('y', d => {
          if (!d.isCopy)
            return -(this.dimension.fontSize * 2.5) / 2;

          return -(this.dimension.fontSize * 2 * 1.7) / 2;
        })
        .attr('rx', 3)
        .attr('width', getWidthForDimension.bind(this))
        .attr('height', d => {
          if (!d.isCopy)
            return this.dimension.fontSize * 2.5;

          return this.dimension.fontSize * 2 * 1.7;
        })
        .attr('fill', circleColour.bind(this))
        .attr('stroke', '#AE5A21');

      // Draw text for dimensions
      dimensions.append('text')
        .selectAll()
        .data(getText.bind(this))
        .enter()
        .append('tspan')
        .attr('class', 'dim-text')
        .style('font-size', `${this.dimension.fontSize}px`)
        .text(d => d.name)
        .attr('x', 0)
        .attr('y', 2)
        .attr('dy', d => d.offset);

      // Get attributes
      const attributes = this.d3.node.filter(d => this.isAttribute(d.type))
        .filter(d => this.isFact(d.parentType) ? this.isDoNotAggregate(d.aggregationType) : true);

      // Draw circle for attributes
      attributes.append('circle')
        .attr('r', this.d3.radiusAttribute)
        .attr('fill', circleColour.bind(this))
        .attr('stroke', '#AEAE21');

      // Draw text for attributes
      attributes.append('text')
        .selectAll()
        .attr('dx', 18)
        .attr('dy', '.35em')
        .data(getText.bind(this))
        .enter()
        .append('tspan')
        .attr('class', d => d.class)
        .style('font-size', `${this.attribute.fontSize}px`)
        .text(d => d.name)
        .attr('x', 16)
        .attr('y', 0)
        .attr('dy', d => d.offset);

      if (!this.hoverTooltipDiv) {
        this.hoverTooltipDiv = d3.select('#pageContent')
          .append('div')
          .attr('class', 'svg-tooltip')
          .style('display', 'none');

      }

      this.d3.node.on('mouseover', d => {
        this.d3.svg.style('cursor', 'pointer');
        this.displayTooltip(d, d3.event.clientX, d3.event.clientY);
      }).on('mouseout', d => {
        this.d3.svg.style('cursor', 'auto');
        this.d3.node.selectAll('circle').style('opacity', 1);
        this.d3.node.selectAll('rect').style('opacity', 1);
        this.d3.node.selectAll('text').style('opacity', 1);
        this.d3.link.style('opacity', 1);

        this.hoverTooltipDiv.style('display', 'none');
      }).on('mousedown', d => {

      }).on('mousedown', d => {

      }).on('dblclick', d => {
        if (this.isFact(d.type)) {

          //reset zoom on double click
          const zoom = d3.zoom()
            .on('zoom', () => {
              this.d3.svg.attr('transform', d3.event.transform);
            });
          this.d3.svg.on('dblclick.zoom', null);
          this.d3.svg.call(zoom.transform, d3.zoomIdentity.scale(1));
          this.hoverTooltipDiv.style('display', 'none');

          const collapsed = !this.optionsFilter.filteredFacts ? [d.entityId] : null;
          this.updateFilter({filteredFacts: collapsed});

        }

      });

      function getWidthForDimension(d) {
        const textWidth = pixelWidth(d.entityDisplayName, {size: this.dimension.fontSize});
        let secondaryTextWidth = 0;
        if (d.mainDimName) {
          secondaryTextWidth = pixelWidth(d.mainDimName, {size: this.dimension.fontSize});
        }
        const width = Math.max(textWidth, secondaryTextWidth);
        if (width <= this.dimension.maxWidth && width >= this.dimension.minWidth) {
          return width + 10;
        } else if (width > this.dimension.maxWidth) {
          return this.dimension.maxWidth;
        } else { //  if (textWidth < this.dimension.minWidth)
          return this.dimension.minWidth;
        }
      }

      //Function to choose what color circle we have
      //Let's return blue for males and red for females and red for attributes
      function circleColour(d) {
        if (this.isFact(d.type)) {
          return '#4472C4';
        } else if (this.isDim(d.type)) {
          return '#ED7D31';
        } else {
          return '#EDED31';
        }
      }

      function linkColor(d) {
        return this.isAttribute(d.type) ? '#AEAE21' : '#AE5A21';
      }

      function getText(d) {
        const data = [];
        if (this.isFact(d.type)) {
          const hasAttributes = d.attributes && d.attributes.filter(a => !this.isDoNotAggregate(a.aggregationType)).length > 0;
          d.radius = hasAttributes ? d.attributes.length > 25 ? d.attributes.length * 3 : d.attributes.length * 4 < 30 ? 30 : d.attributes.length * 4 : d.radius;
          data.push({
            name: this.ellipsisText(d.entityDisplayName, d.radius, d.type),
            offset: hasAttributes ? -2 : 0,
            type: d.type,
            class: 'fact-text',
            fontSize: this.fact.factFontSize,
            x: 0,
            y: hasAttributes ? -(d.radius - 20) : 0,
          });
          if (hasAttributes) {
            const attributes = d.attributes.filter(a => !this.isDoNotAggregate(a.aggregationType));
            for (let i = 0; i < attributes.length; i++) {
              const dy = (i + 1) * 5;
              data.push({
                name: this.ellipsisText(attributes[i].attributeDisplayName, d.radius),
                offset: dy,
                type: 'attribute',
                isFromFact: true,
                class: 'attribute-text',
                fontSize: this.fact.attrFontSize,
                x: 0,
                y: hasAttributes ? -(d.radius - 21) : 0,
              });
            }
          }
        } else if (this.isDim(d.type)) {
          const dimText = {
            name: this.ellipsisText(d.entityDisplayName, this.dimension.area, d.type),
            offset: 0,
            class: 'node',
          };
          if (d.isCopy) {
            dimText.offset = -this.dimension.fontSize / 2;
            data.push({
              name: this.ellipsisText(`(${d.mainDimName})`, 15, d.type),
              offset: this.dimension.fontSize / 2 + 2, // Some weird calculation just to make sure it looks good always when you change font size of dimension
            });
          }
          data.push(dimText);
        } else if (this.isAttribute(d.type)) {
          data.push({
            name: d.attributeDisplayName,
            offset: 0,
            class: 'node',
          });
        }
        return data;
      }
    },
    ellipsisText(text, width, type = null) {
      let newText = text;
      if (type && this.isDim(type)) {
        const textWidth = pixelWidth(text, {size: this.dimension.fontSize});
        if (textWidth > this.dimension.maxWidth) {
          newText = this.substringTextForWidth(text, this.dimension.maxWidth - 20);
          // let sliceChar = text.length - (text.length - 5)
          newText += `${newText !== text ? '...' : ''}`;
        }
      } else {
        const size = type ? this.fact.factFontSize : this.fact.attrFontSize;
        if (pixelWidth(text, {size: size}) - 5 > width) {
          const divNum = size / 2;
          const sliceChar = text.length - (text.length - width / divNum);
          newText = `${text.slice(0, sliceChar)}${sliceChar === text.length ? '' : '...'}`;
        }
      }
      return newText;
    },
    substringTextForWidth(text, width) {
      let reducedWidth;
      do {
        text = text.substr(0, text.length - 1);
        reducedWidth = pixelWidth(text, {size: this.dimension.fontSize});
      } while (reducedWidth > width);
      return text;
    },
    isConnected(d, o) {
      let identifier = false;
      const links = this.sunModelData.links.length === 0 && this.links.length > 0 ? [...this.links] : [...this.sunModelData.links];
      links.forEach(link => {
        if (this.isFact(d.type) && ((this.isDim(o.type) && link.fromEntityId === d.entityId && link.toEntityId === o.entityId) || d.entityId === o.entityId) ||
            this.isDim(d.type) && ((this.isDim(o.type) && link.fromEntityId === d.entityId && link.toEntityId === o.entityId) || d.entityId === o.entityId) ||
            this.isDim(d.type) && ((this.isFact(o.type) && link.fromEntityId === o.entityId && link.toEntityId === d.entityId) || d.entityId === o.entityId) ||
            this.isDim(d.type) && ((this.isDim(o.type) && link.fromEntityId === o.entityId && link.toEntityId === d.entityId) || d.entityId === o.entityId) ||
            this.isAttribute(d.type) && (o.attributes && o.attributes.find(attr => attr.attributeId === d.attributeId) || d.attributeId === o.attributeId)) {
          identifier = true;
        }
      });
      return identifier;
    },
    initEventListeners() {
      // Get default coordinates
      if (this.coordinates.length > 0) {
        this.nodes.forEach(d => {
          const entityId = d.isCopy ? d.fromEntityId : this.isAttribute(d.type) ? d.attributeId : d.entityId;
          const coordinate = this.coordinates.find(item => item.entityId === entityId);
          if (coordinate) {
            if (d.isCopy) {
              const max = 200;
              const min = 100;
              const randomNumber = Math.floor(Math.random() * (max - min) + min);
              d.fx = coordinate.x + randomNumber;
              d.fy = coordinate.y + randomNumber;
            } else {
              d.fx = coordinate.x;
              d.fy = coordinate.y;
            }
          }
        });
      }
      //add drag capabilities
      const drag_handler = d3.drag()
        .on('start', drag_start.bind(this))
        .on('drag', drag_drag.bind(this))
        .on('end', drag_end.bind(this));

      drag_handler(this.d3.node);

      //add zoom capabilities
      const zoom_handler = d3.zoom()
        .on('zoom', zoom_actions.bind(this));

      zoom_handler(this.d3.svg);

      // Add zoom in and out for zoom buttons
      const svg = this.d3.svg;
      zoom_handler(svg);

      d3.select('#zoom_in').on('click', () => {
        zoom_handler.scaleBy(svg.transition().duration(750), 1.3);
      });

      d3.select('#zoom_out').on('click', () => {
        zoom_handler.scaleBy(svg.transition().duration(750), 1 / 1.3);
      });

      /** Functions **/

      //Drag functions
      //d is the node
      function drag_start(d) {
        this.nodes.forEach(n => {
          n.fx = n.x;
          n.fy = n.y;
        });
        if (!d3.event.active) this.d3.simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      }

      //make sure you can't drag the circle outside the box
      function drag_drag(d) {
        d.fx = d3.event.x;
        d.fy = d3.event.y;
        this.hoverTooltipDiv.style('display', 'none');
        // this.displayTooltip(this.hoverTooltipDiv,d);
      }

      function drag_end(d) {
        if (!d3.event.active) this.d3.simulation.alphaTarget(0);
        this.onCoordinateChange();
        // d.fx = null;
        // d.fy = null;
        // this.hoverTooltipDiv.attr('style','none')

      }

      //Zoom functions
      function zoom_actions() {

        this.transform = d3.event.transform;
        this.d3.g.attr('transform', d3.event.transform);
      }
    },
    onCoordinateChange() {
      this.nodes.forEach(item => {
        if (!item.isCopy) {
          const entityId = this.isAttribute(item.type) ? item.attributeId : item.entityId;
          const index = this.coordinates.findIndex(c => c.entityId === entityId);
          if (index === -1) {
            this.coordinates.push({
              entityId: entityId,
              x: item.fx,
              y: item.fy,
            });
          } else {
            this.coordinates[index].x = item.fx;
            this.coordinates[index].y = item.fy;
          }
        }
      });
      localStorage.setItem('SUN_MODEL_COORDINATES', JSON.stringify(this.coordinates));
    },
    getCoordinates() {
      const coordinates = localStorage.getItem('SUN_MODEL_COORDINATES');
      if (coordinates) {
        this.coordinates = JSON.parse(coordinates);
      }
    },
    getSliderOptions() {
      const sliderOptions = JSON.parse(localStorage.getItem('SLIDER_OPTIONS'));
      if (sliderOptions) {
        this.dimension = {...sliderOptions.dimension};
        this.attribute = {...sliderOptions.attribute};
        this.fact = {...sliderOptions.fact};
        this.d3.radiusFact = this.fact.radius;
        this.d3.fontSizeFact = this.fact.factFontSize;
      }
    },
    resetView() {
      localStorage.removeItem('SUN_MODEL_COORDINATES');
      localStorage.removeItem('SLIDER_OPTIONS');
      this.nodes.forEach(d => {
        delete d.fx;
        delete d.fy;
      });
      this.coordinates = [];
      this.dimension = dimensionDefaults;
      this.attribute = {
        distance: 150,
        strokeWidth: 1,
        fontSize: 12,
      };
      this.fact = {
        factFontSize: 8,
        attrFontSize: 4,
        radius: 30,
      };
      this.d3.radiusFact = 30;
      this.d3.fontSizeFact = 8;
      this.filterData();
    },
    highlightCircle(node) {
      const lines = this.$refs.line;
      const circles = this.$refs.circle;
      const highLightCircle = [];
      for (const index in lines) {
        const sourceId = parseInt(lines[index].getAttribute('data-source-id'));
        const targetId = parseInt(lines[index].getAttribute('data-target-id'));
        lines[index].classList.remove('highLight');
        lines[index].classList.remove('hidden');

        if (sourceId == node.index || targetId == node.index) {
          highLightCircle.push(sourceId);
          highLightCircle.push(targetId);
          lines[index].classList.add('highLight');
        } else {
          lines[index].classList.add('hidden');
        }
      }
      for (const k in circles) {
        circles[k].classList.remove('highLight');
        circles[k].classList.add('hidden');
      }
      for (const circleIndex in highLightCircle) {
        circles[highLightCircle[circleIndex]].classList.add('highLight');
        circles[highLightCircle[circleIndex]].classList.remove('hidden');
      }
    },
    calculateDimensions() {
      this.width = this.$refs.svgWrapper.offsetWidth;
      this.height = this.$refs.svgWrapper.offsetHeight;
    },
    onResize() {
      this.calculateDimensions();
    },
    onFactAdd(fact) {
      fact.type = 'fact';
      this.nodes.push(fact);
      this.renderGraph();
    },
    onDimensionAdd(dimension) {
      dimension.type = 'dimension';
      this.nodes.push(dimension);
      this.renderGraph();
    },
    onLinkAdd(link) {
      link.source = link.fromEntityId;
      link.target = link.toEntityId;
      this.links.push(link);
      this.renderGraph();
    },
    showAttributes() {
      this.attributes = !this.attributes;
      this.renderGraph();
    },
    displayMeasures() {
      this.showMeasures = !this.showMeasures;
      this.renderGraph();
    },
    displayMinor() {
      this.showMinor = !this.showMinor;
      this.renderGraph();
    },
    dimensionLinkAdd(data) {
      data.dimension.type = 'dimension';
      data.link.source = data.link.fromEntityId;
      data.link.target = data.link.toEntityId;
      this.nodes.push(data.dimension);
      this.links.push(data.link);
      this.renderGraph();
    },
    isAttribute(type) {
      return type === 'attribute';
    },
    isFact(type) {
      return type === 'fact';
    },
    isDim(type) {
      return type === 'dimension';
    },
    isDoNotAggregate(type) {
      return type === 'DoNotAggregate';
    },
    onSlideOptionsClick() {
      const params = {
        dimension: {
          width: this.dimension.strokeWidth,
          length: this.dimension.distance,
          fontSize: this.dimension.fontSize,
        },
        attribute: {
          width: this.attribute.strokeWidth,
          length: this.attribute.distance,
          fontSize: this.attribute.fontSize,
        },
        fact: {
          fontSize: this.fact.factFontSize,
        },
      };
      this.showSliderModal(params);
    },
    onManageTagsClick() {
      this.showTagsModal();
    },
    saveSlideOptions() {
      const params = {
        dimension: {...this.dimension},
        attribute: {...this.attribute},
        fact: {...this.fact},
      };
      localStorage.setItem('SLIDER_OPTIONS', JSON.stringify(params));
    },
    onLengthChange(length, isAttr) {
      length = parseInt(length);
      if (isAttr) {
        this.attribute.distance = length;
      } else {
        this.dimension.distance = length;
      }
      this.coordinates = [];
      this.saveSlideOptions();
      this.filterData();
    },
    onWidthChange(width, isAttr) {
      width = parseInt(width);
      if (isAttr) {
        this.attribute.strokeWidth = width;
      } else {
        this.dimension.strokeWidth = width;
      }
      this.saveSlideOptions();
      this.filterData();
    },
    onFontSizeChange(fontSize, isAttr) {
      fontSize = parseInt(fontSize);
      if (isAttr) {
        this.attribute.fontSize = fontSize;
      } else {
        // Get dimension texts
        const dimText = document.getElementsByClassName('dim-text');
        const dimLengths = [];
        // Push all text in array
        for (const index in dimText) {
          if (dimText.hasOwnProperty(index)) {
            const textLength = pixelWidth(dimText[index].textContent, {size: fontSize});
            dimLengths.push(textLength);
          }
        }
        // Get text max length for dimension width
        const textMaxLength = Math.round(Math.max(...dimLengths));

        this.dimension.area = textMaxLength > 40 ? textMaxLength : 40;
        this.dimension.fontSize = fontSize;
      }
      this.saveSlideOptions();
      this.filterData();
    },
    onFactFontSizeChange(fontSize) {
      fontSize = parseInt(fontSize);
      const factText = document.getElementsByClassName('fact-text');
      const factLengths = [];
      for (const index in factText) {
        if (factText.hasOwnProperty(index)) {
          const textLength = pixelWidth(factText[index].textContent, {size: fontSize});
          factLengths.push(textLength);
        }
      }
      const textMinLength = Math.round(Math.min(...factLengths));

      this.fact.radius = textMinLength > 30 ? textMinLength : 30;
      this.fact.factFontSize = fontSize;
      this.d3.radiusFact = this.fact.radius;
      this.d3.fontSizeFact = fontSize;
      this.saveSlideOptions();
      this.filterData();
    },
    computedDimensionArrowXCoordinate(dimStrokeWidth, dimArea) {
      if (dimArea < 41) {
        return [1, 2].includes(dimStrokeWidth) ? 55 : dimStrokeWidth === 3 ? 40 : dimStrokeWidth === 4 ? 35 : 30;
      } else if (dimArea < 51) {
        return [1, 2].includes(dimStrokeWidth) ? 60 : dimStrokeWidth === 3 ? 45 : dimStrokeWidth === 4 ? 40 : 35;
      } else if (dimArea < 56) {
        return [1, 2].includes(dimStrokeWidth) ? 70 : dimStrokeWidth === 3 ? 50 : dimStrokeWidth === 4 ? 43 : 37;
      } else if (dimArea < 61) {
        return [1, 2].includes(dimStrokeWidth) ? 75 : dimStrokeWidth === 3 ? 55 : dimStrokeWidth === 4 ? 45 : 40;
      } else if (dimArea < 66) {
        return [1, 2].includes(dimStrokeWidth) ? 80 : dimStrokeWidth === 3 ? 60 : dimStrokeWidth === 4 ? 47 : 40;
      } else if (dimArea < 71) {
        return [1, 2].includes(dimStrokeWidth) ? 85 : dimStrokeWidth === 3 ? 60 : dimStrokeWidth === 4 ? 47 : 43;
      } else if (dimArea < 76) {
        return [1, 2].includes(dimStrokeWidth) ? 90 : dimStrokeWidth === 3 ? 65 : dimStrokeWidth === 4 ? 50 : 45;
      } else if (dimArea < 81) {
        return [1, 2].includes(dimStrokeWidth) ? 95 : dimStrokeWidth === 3 ? 70 : dimStrokeWidth === 4 ? 55 : 47;
      } else if (dimArea < 86) {
        return [1, 2].includes(dimStrokeWidth) ? 100 : dimStrokeWidth === 3 ? 70 : dimStrokeWidth === 4 ? 57 : 50;
      }
    },
    _showHideAttributes() {
      this.showHideAttributes(!this.optionsFilter.displayAttributes);
    },
    _showHideMinorElements() {
      this.showHideMinorElements(!this.optionsFilter.displayMinorElements);
    },
  },
  async mounted() {
    this.getTags();
    this.getCoordinates();
    this.getSliderOptions();
    await this.loadData();
    this.calculateDimensions();
    window.addEventListener('resize', this.onResize);
    await this.getAggregationTypes();
    await this.getDataTypes();

    eventBus.$on(DIMENSION_LINK_ADD, this.dimensionLinkAdd);
    eventBus.$on(FACT_ADD, this.onFactAdd);
    eventBus.$on(DIMENSION_ADD, this.onDimensionAdd);
    eventBus.$on(LINK_ADD, this.onLinkAdd);
    eventBus.$on(SHOW_ATTRIBUTES, this.showAttributes);
    eventBus.$on(SHOW_MEASURES, this.displayMeasures);
    eventBus.$on(SHOW_MINOR, this.displayMinor);
  },
  destroyed() {
    this.unloadData();
    window.removeEventListener('resize', this.onResize);
  },
};
</script>

<style lang="scss">
.tags-select {
  width: 350px;
  display: block;
  height: 42px;

  .multiselect__tags {
    position: relative;
    max-height: 43px;
    overflow: auto;
  }
}

.fact-text {
  text-anchor: middle;
  fill: white;
  font-weight: bold;
}

.dim-text {
  fill: white;
  text-anchor: middle;
  display: inline-block;
}

.attribute-text {
  text-anchor: middle;
  fill: white;
}

#sunModel {
  .btn-zoom {
    border-radius: 5px;
  }

  .page-content {
    position: relative;
  }

  .dashboard-item {
    height: 100%;
  }

  .dark-mode {
    background-color: #121212;
    color: #000;

    tspan {
      fill: #FFF;
    }
  }

  .dashboard-item {
    height: 100%;
  }

  .home-page {
    flex: 1;
    overflow: auto;
  }

  .svg-wrapper {
    height: 100%;
  }

  svg {
    position: relative;

    circle {
      cursor: pointer;

      &.highLight {
        opacity: 1;
      }

      &.hidden {
        opacity: 0.5;
      }
    }

    line {
      &.highLight {
        opacity: 1;
      }

      &.hidden {
        opacity: 0.5;
      }
    }
  }

  .svg-tooltip {
    border: 2px solid black;
    border-radius: 5px;
    position: absolute;
    text-align: left;
    width: auto;
    height: auto;
    padding: 4px;
    background: white;

    p {
      margin: 0;
    }
  }
}
</style>
